import React, { useMemo, useState } from "react";
import { Search, GripVertical, ChevronRight, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { IsoThumb } from "./IsoThumb";
import { dragState, LIBRARY_FILTERS } from "./rigGeometry";

const fmtM = (v) => Number(v).toLocaleString("nb-NO", { maximumFractionDigits: 1 });

/** Plan-view footprint (kept for callers that want a flat marker). */
export const Footprint = ({ type, size = 28, className }) => {
  const w = Number(type.width_m) || 3;
  const l = Number(type.length_m) || 8.4;
  const max = Math.max(w, l);
  const pw = Math.max(6, Math.round((w / max) * size));
  const pl = Math.max(6, Math.round((l / max) * size));
  return (
    <div className={cn("flex shrink-0 items-center justify-center", className)} style={{ width: size + 6, height: size + 6 }} aria-hidden>
      <div className="rounded-[2px] border" style={{ width: pl, height: pw, background: type.color, borderColor: "rgba(15,23,42,0.28)" }} />
    </div>
  );
};

const Chip = ({ active, onClick, children, testId, count }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    data-testid={testId}
    className={cn(
      "inline-flex h-6 shrink-0 items-center gap-1 rounded-full px-2.5 text-[11.5px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
      active ? "bg-foreground text-background" : "bg-muted/70 text-muted-foreground hover:bg-muted hover:text-foreground"
    )}
  >
    {children}
    {count != null && <span className={cn("tabular", active ? "text-background/70" : "text-muted-foreground/70")}>{count}</span>}
  </button>
);

/** One module type as a compact product object. Click = arm placement, double-click = add, drag = drop into scene. */
const TypeCard = ({ t, active, disabled, onPick, onAdd }) => {
  const avail = Number(t.fleet_available) || 0;
  const total = Number(t.fleet_total) || 0;
  return (
    <button
      type="button"
      draggable={!disabled}
      disabled={disabled}
      onDragStart={(e) => { dragState.typeId = t.id; e.dataTransfer.setData("application/x-module-type", t.id); e.dataTransfer.effectAllowed = "copy"; }}
      onDragEnd={() => { dragState.typeId = null; }}
      onClick={() => onPick?.(active ? null : t.id)}
      onDoubleClick={() => onAdd?.(t.id)}
      title={disabled ? "Kun lesetilgang" : `${t.name}\nKlikk for å plassere · dobbeltklikk legger til · dra inn i scenen`}
      className={cn(
        "group relative flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left transition-colors duration-150",
        "hover:bg-muted/70 focus-visible:outline-none focus-visible:bg-muted/70 disabled:cursor-default disabled:opacity-60",
        active && "bg-primary/[0.07] ring-1 ring-primary/40 hover:bg-primary/[0.09]"
      )}
      data-testid={`rig-library-item-${t.code}`}
      aria-pressed={active}
    >
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-muted/60">
        <IsoThumb type={t} size={40} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-[12.5px] font-medium leading-4">{t.name}</span>
          {total > 0 && (
            <span className={cn("shrink-0 text-[11px] tabular", avail > 0 ? "text-muted-foreground" : "text-amber-700")} data-testid={`rig-library-avail-${t.code}`}>
              {avail} ledig
            </span>
          )}
        </div>
        <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground tabular">
          <span className="font-mono">{t.code}</span>
          <span aria-hidden>·</span>
          <span>{fmtM(t.width_m)} × {fmtM(t.length_m)} m</span>
          {t.beds > 0 && <><span aria-hidden>·</span><span>{t.beds} senger</span></>}
          {!t.beds && t.capacity_persons > 0 && <><span aria-hidden>·</span><span>{t.capacity_persons} pers.</span></>}
        </div>
      </div>
      <GripVertical className="absolute right-1 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground/50 opacity-0 transition-opacity duration-150 group-hover:opacity-100" aria-hidden />
    </button>
  );
};

/**
 * Module library: a kit of parts. Filter chips narrow by category, groups collapse, cards are draggable.
 */
export function ModuleLibrary({ library, loading, pendingType, onPick, onAdd, disabled, className }) {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("alle");
  const [onlyAvailable, setOnlyAvailable] = useState(false);
  const [closed, setClosed] = useState(() => new Set());

  const filters = useMemo(() => {
    const items = library?.items || [];
    return LIBRARY_FILTERS.map((f) => ({ ...f, count: f.kinds ? items.filter((t) => f.kinds.includes(t.kind)).length : items.length })).filter((f) => f.count > 0);
  }, [library]);

  const groups = useMemo(() => {
    const term = q.trim().toLowerCase();
    const kinds = LIBRARY_FILTERS.find((f) => f.id === filter)?.kinds || null;
    return (library?.groups || [])
      .filter((g) => !kinds || kinds.includes(g.kind))
      .map((g) => ({
        ...g,
        items: g.items.filter((t) => (!term || `${t.code} ${t.name} ${t.kind_label}`.toLowerCase().includes(term)) && (!onlyAvailable || (Number(t.fleet_available) || 0) > 0)),
      }))
      .filter((g) => g.items.length);
  }, [library, q, filter, onlyAvailable]);
  const shown = groups.reduce((s, g) => s + g.items.length, 0);

  return (
    <aside className={cn("flex h-full min-h-0 flex-col bg-background", className)} data-testid="rig-library">
      <div className="px-3 pb-2 pt-3">
        <div className="flex items-baseline justify-between px-0.5">
          <h2 className="text-[12.5px] font-medium">Bibliotek</h2>
          <span className="text-[11px] text-muted-foreground tabular" data-testid="rig-library-count">{loading ? "…" : `${shown} av ${library?.items?.length ?? 0} typer`}</span>
        </div>
        <div className="relative mt-2">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk modultype…" className="h-8 border-transparent bg-muted/60 pl-7 pr-7 text-[12.5px] focus-visible:border-input focus-visible:bg-background" data-testid="rig-library-search" aria-label="Søk modultype" />
          {q && <button type="button" onClick={() => setQ("")} className="absolute right-1.5 top-1/2 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Tøm søk" data-testid="rig-library-clear"><X className="h-3.5 w-3.5" /></button>}
        </div>
        <div className="mt-2 flex items-center gap-1 overflow-x-auto pb-0.5 [&::-webkit-scrollbar]:hidden" data-testid="rig-library-filters">
          {filters.map((f) => <Chip key={f.id} active={filter === f.id} onClick={() => setFilter(f.id)} testId={`rig-library-filter-${f.id}`}>{f.label}</Chip>)}
        </div>
        <label className="mt-2 flex h-6 cursor-pointer items-center gap-2 px-0.5 text-[11.5px] text-muted-foreground select-none">
          <input type="checkbox" checked={onlyAvailable} onChange={(e) => setOnlyAvailable(e.target.checked)} className="h-3.5 w-3.5 rounded border-input accent-[hsl(var(--primary))]" data-testid="rig-library-only-available" />
          Kun typer med ledige enheter
        </label>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin px-2 pb-2" data-testid="rig-library-list">
        {loading && <div className="space-y-1.5 px-1 pt-1">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}</div>}
        {!loading && !groups.length && (
          <div className="px-3 py-10 text-center text-[12.5px] leading-5 text-muted-foreground" data-testid="rig-library-empty">
            Ingen modultyper matcher{q ? <> «{q}»</> : " filteret"}.
            <div className="mt-2"><button type="button" className="link" onClick={() => { setQ(""); setFilter("alle"); setOnlyAvailable(false); }} data-testid="rig-library-reset">Nullstill filter</button></div>
          </div>
        )}
        {groups.map((g) => {
          const open = !closed.has(g.kind);
          return (
            <Collapsible key={g.kind} open={open} onOpenChange={(o) => setClosed((s) => { const n = new Set(s); o ? n.delete(g.kind) : n.add(g.kind); return n; })} className="mb-1">
              <CollapsibleTrigger className="flex h-7 w-full items-center gap-1 rounded px-1.5 text-left text-[11px] font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:bg-muted/60" data-testid={`rig-library-group-${g.kind}`}>
                <ChevronRight className={cn("h-3 w-3 transition-transform duration-150", open && "rotate-90")} />
                <span className="flex-1">{g.label}</span>
                <span className="tabular">{g.items.length}</span>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-px">
                {g.items.map((t) => <TypeCard key={t.id} t={t} active={pendingType === t.id} disabled={disabled} onPick={onPick} onAdd={onAdd} />)}
              </CollapsibleContent>
            </Collapsible>
          );
        })}
      </div>
      <div className="px-3 pb-3 pt-1 text-[11px] leading-4 text-muted-foreground">
        {disabled ? "Lesetilgang – biblioteket er låst." : <>Klikk en type og plasser den i scenen, eller dra den rett inn. <span className="whitespace-nowrap"><kbd className="kbd">R</kbd> roterer før plassering.</span></>}
      </div>
    </aside>
  );
}
